import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Avatar,
    Typography,
    Divider,
    Button,
    Modal,
    Box,
    CircularProgress,
    Alert,
    IconButton,
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchCopyById } from '../api/copyApi';
import { formatDate } from '../utils/dateUtils';
import RedoCopy from './RedoCopy';

const CopyCard = ({ id, title, summary, creationDate, user, handleDelete, onCopyListUpdate }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState(null);
    const [currentCopyId, setCurrentCopyId] = useState(id);
    const [hasRedoOccurred, setHasRedoOccurred] = useState(false);

    const fetchCopyDetails = async (copyId) => {
        setLoading(true);
        setError(null);
        try {
            console.log('Fetching copy details for ID:', copyId);
            const response = await fetchCopyById(copyId);
            console.log('Received copy details:', response);
            if (Array.isArray(response) && response.length > 0) {
                setDetails(response[0]);
            } else {
                throw new Error('Unexpected response format');
            }
        } catch (err) {
            console.error('Error fetching copy details:', err);
            setError('Failed to fetch details');
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        await fetchCopyDetails(currentCopyId);
    };

    const handleClose = () => {
        setOpen(false);
        setDetails(null);
        // If a redo operation occurred, refresh the copy list
        if (hasRedoOccurred && onCopyListUpdate) {
            onCopyListUpdate();
            setHasRedoOccurred(false);
        }
    };

    const handleCopyToClipboard = () => {
        if (details && details.copyText) {
            navigator.clipboard.writeText(details.copyText).then(
                () => {
                    alert('Text copied to clipboard!');
                },
                (err) => {
                    console.error('Failed to copy text:', err);
                }
            );
        }
    };

    return (
        <>
            <Card
                sx={{
                    maxWidth: 345,
                    margin: 'auto',
                    transition: '0.3s',
                    boxShadow: '0 8px 40px -12px rgba(0,0,0,0.3)',
                    '&:hover': {
                        boxShadow: '0 16px 70px -12.125px rgba(0,0,0,0.3)',
                    },
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: deepPurple[500] }}>
                            {user ? user.charAt(0).toUpperCase() : 'U'}
                        </Avatar>
                    }
                    title={title || 'Untitled'}
                    subheader={`User ID: ${user || 'Unknown'}`}
                />
                <Divider variant="middle" />
                <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {summary || 'No summary available.'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Created on: {formatDate(creationDate)}
                    </Typography>
                </CardContent>
                <CardActions>
                    <Button size="small" onClick={handleOpen}>
                        Detail
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(id)}>
                        Delete
                    </Button>
                </CardActions>
            </Card>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="copy-detail-title"
                aria-describedby="copy-detail-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        maxWidth: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography id="copy-detail-title" variant="h6" component="h2">
                            Copy Detail
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    ) : details ? (
                        <>
                            <Box id="copy-detail-description" sx={{ mt: 2 }}>
                                <RedoCopy
                                    copyId={currentCopyId}
                                    copyText={details.copyText}
                                    onRedoComplete={async (result) => {
                                        // Handle the redo result - new copy document with new ID
                                        console.log('Redo completed:', result);
                                        if (result && result.id) {
                                            // Update the current copy ID to the new one
                                            setCurrentCopyId(result.id);
                                            // Mark that a redo operation has occurred
                                            setHasRedoOccurred(true);
                                            // Refresh the copy details with the new ID
                                            await fetchCopyDetails(result.id);
                                        }
                                    }}
                                />
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="textSecondary">
                                Summary: {details.copySummary || 'No summary available.'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Created on: {formatDate(details.creationDate)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                By: {details.userIdentifier || 'Unknown'}
                            </Typography>
                        </>
                    ) : null}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyToClipboard}
                            disabled={!details || !details.copyText}
                        >
                            Copy
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default CopyCard;
